import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Waves, Shield, Star, TrendingUp, ArrowLeft } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import PasswordInput from '../components/PasswordInput';
import { setToken } from '../lib/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'; // fallback dev only

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await login(email, password);
      if(data.user.role === 'provider') navigate('/provider/dashboard');
      else if(data.user.role === 'admin') navigate('/admin/dashboard');
      else navigate('/client/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.errorLogin'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/google`, {
        token: credentialResponse.credential
      }, { withCredentials: true });

      if (res.status === 200) {
        if (res.data.token) setToken(res.data.token);
        if(res.data.user.role === 'provider') navigate('/provider/dashboard');
        else if(res.data.user.role === 'admin') navigate('/admin/dashboard');
        else navigate('/client/dashboard');
        window.location.reload();
      }
    } catch (error) {
      console.error('Login failed', error);
      // Antes: alert() nativo, feo y bloqueante. Reusamos el mismo banner
      // de error que ya existe para el login por email/password.
      setError(t('auth.googleError'));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* Left side - Login */}
        <div className="flex items-center justify-center p-8 lg:p-12 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-6 left-6 flex items-center gap-1.5 text-gray-500 hover:text-[#134168] transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.goBack', 'Volver')}
          </button>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="RedMobility" className="h-24 md:h-28 max-w-none object-contain" />
              </div>
              <p className="text-gray-600 text-lg">{t('auth.loginTitle')}</p>
            </div>

            {error && <div className="mb-4 text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('auth.email')}</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="mt-1 block w-full rounded-xl border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-[#134168]" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('auth.password')}</label>
                <PasswordInput value={password} onChange={e => setPassword(e.target.value)} required className="mt-1 block rounded-xl border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-[#134168]" />
              </div>

              <button type="submit" disabled={loading} className="w-full bg-[#134168] text-white py-3 px-4 rounded-full font-bold hover:bg-[#1a5490] transition disabled:opacity-50">
                {loading ? t('auth.loginInProgress') : t('auth.loginBtn')}
              </button>
            </form>

            <div className="text-center mb-4">
              <button type="button" onClick={() => navigate('/forgot-password')} className="text-gray-400 text-sm hover:text-[#134168] transition-colors">
                {t('auth.forgotPassword')}
              </button>
            </div>

            <div className="text-center mb-6">
              <span className="text-gray-500 text-sm">{t('auth.noAccount')} </span>
              <button type="button" onClick={() => navigate('/register')} className="text-[#134168] text-sm font-medium hover:underline">
                {t('auth.registerHere')}
              </button>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
              <div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">{t('auth.continueWith')}</span></div>
            </div>

            <div className="w-full flex justify-center mb-6" data-testid="google-login-btn">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.log('Login Failed')}
                theme="filled_blue"
                size="large"
                text="continue_with"
                shape="pill"
              />
            </div>

            <div className="text-center text-sm text-gray-500">
              {t('auth.agreeToTerms')}
            </div>
          </motion.div>
        </div>

        {/* Right side - Hero Image */}
        <div className="hidden lg:block relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1764056230965-570f10082acc?w=1200&q=85"
            alt="Jet Ski Caribbean"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

          <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
            <h2 className="text-4xl font-heading font-bold mb-6">{t('auth.heroTagline')}</h2>

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-[#134168] flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">{t('auth.heroVerified')}</h3>
                  <p className="text-sm text-gray-300">{t('auth.heroVerifiedDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Star className="w-6 h-6 text-[#134168] flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">{t('auth.heroRating')}</h3>
                  <p className="text-sm text-gray-300">{t('auth.heroRatingDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Waves className="w-6 h-6 text-[#134168] flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">{t('auth.heroPayments')}</h3>
                  <p className="text-sm text-gray-300">{t('auth.heroPaymentsDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <TrendingUp className="w-6 h-6 text-[#134168] flex-shrink-0" />
                <div>
                  <h3 className="font-bold mb-1">{t('auth.heroRentals')}</h3>
                  <p className="text-sm text-gray-300">{t('auth.heroRentalsDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
